import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { DeviceDto, GameAccountDto, GameDto } from '@gc/shared';

import { api } from '@/lib/api';
import { Field, ghostPillClass, inputClass, pillClass } from './controls';
import { deviceOptionGroups } from './gameAccountBulk';
import {
  buildCreateGameAccountPayload,
  buildUpdateGameAccountPayload,
  isOwnerOnlyRefusal,
  validateGameAccountPayload,
  type GameAccountDraft,
} from './gameAccountForm';
import { toFieldErrors, useResource, type FieldError } from './useSettings';

/**
 * Game Account form (SPEC §6.5).
 *
 * Fields: Account name · Username · Password · Account type (company) · Email ·
 * Mailbox password · Made for PC · Notes · Games this account owns.
 *
 * The per-game "Devices allowed" ticks went with the borrow system (owner,
 * 2026-10-05): only the lease ever read them. Saving no longer sends
 * `deviceAllowances`, which the update schema reads as "leave them" — the
 * rows already stored stay as they are, unread, so a rollback still finds
 * them. What an account owns matters more than before: Play on a Steam game
 * picks, among the PC's own accounts, one that owns the game.
 *
 * Email, mailbox password and notes (owner, 2026-09-08): a launcher account
 * is only as recoverable as the mailbox it was registered with, so the venue
 * keeps that mailbox's password beside the launcher's. It is write-only here
 * — the DTO says only whether one is stored — and on edit it has three
 * states: blank keeps it, a value replaces it, the clear box posts null.
 * `gameAccountForm.ts` builds and validates the body; this file only holds
 * the fields. Only the owner reaches this drawer (see `GameAccountsPage`).
 *
 * Made for PC (owner, 2026-09-23): the PC staff keep this account signed in
 * on (2026-10-05), which that PC's Accounts app lists under "This PC's
 * credentials". PCs first, grouped by family so VPC-3 never hides between
 * PC-29 and PC-3; the first option makes the account venue-wide again.
 */
export function GameAccountDrawer({
  account,
  onClose,
  onSaved,
}: {
  account: GameAccountDto | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = account !== null;

  const games = useResource<GameDto[]>('/settings/games');
  // Hidden machines included: the venue's back-office PC is off the board but
  // may still be the one an account was made on.
  const devices = useResource<{ devices: DeviceDto[] }>('/devices?includeHidden=true');

  const [accountName, setAccountName] = useState(account?.accountName ?? '');
  const [username, setUsername] = useState(account?.username ?? '');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState(account?.company ?? 'Steam');
  const [email, setEmail] = useState(account?.email ?? '');
  // Write-only: the DTO never carries the value, so this starts blank on edit
  // exactly like the account password, and blank means "keep it".
  const [emailPassword, setEmailPassword] = useState('');
  const [clearEmailPassword, setClearEmailPassword] = useState(false);
  const [notes, setNotes] = useState(account?.notes ?? '');
  const [homeDeviceId, setHomeDeviceId] = useState(account?.homeDeviceId ?? '');
  const [gameIds, setGameIds] = useState<Set<string>>(new Set(account?.gameIds ?? []));

  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const errorFor = (field: string) => errors.find((issue) => issue.field === field)?.message;
  const deviceList = devices.data?.devices ?? [];
  const deviceGroups = deviceOptionGroups(deviceList);
  // A PC retired since (not in the active list) still shows as the current
  // choice rather than silently reading as "venue-wide".
  const homeDeviceMissing =
    homeDeviceId !== '' && !deviceList.some((device) => device.id === homeDeviceId);

  function toggleGame(gameId: string, checked: boolean): void {
    setGameIds((current) => {
      const next = new Set(current);
      if (checked) next.add(gameId);
      else next.delete(gameId);
      return next;
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrors([]);

    const draft: GameAccountDraft = {
      accountName,
      username,
      password,
      company,
      gameIds: [...gameIds],
      email,
      emailPassword,
      clearEmailPassword,
      notes,
      homeDeviceId,
    };
    const payload =
      isEdit && account
        ? buildUpdateGameAccountPayload(draft)
        : buildCreateGameAccountPayload(draft);

    // The API's own schema, before the request: a malformed email lands on
    // its field now rather than as a round-trip.
    const refused = validateGameAccountPayload(payload, isEdit ? 'edit' : 'create');
    if (refused.length > 0) {
      setErrors(refused);
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit && account) {
        await api.patch(`/settings/game-accounts/${account.id}`, payload);
      } else {
        await api.post('/settings/game-accounts', payload);
      }
      onSaved();
      onClose();
    } catch (caught) {
      setErrors(
        isOwnerOnlyRefusal(caught)
          ? [{ field: '(root)', message: t('settings.gameAccounts.ownerOnly') }]
          : toFieldErrors(caught, t('settings.gameAccounts.saveFailed')),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex h-full w-full max-w-2xl flex-col border-s border-subtle bg-raised shadow-2xl"
      >
        <header className="border-b border-subtle p-5">
          <h2 className="text-lg font-semibold text-content">
            {isEdit ? t('settings.gameAccounts.edit') : t('settings.gameAccounts.addNew')}
          </h2>
        </header>

        <div className="min-h-0 flex-1 overflow-auto p-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('settings.gameAccounts.accountName')} error={errorFor('accountName')}>
              <input
                value={accountName}
                onChange={(event) => setAccountName(event.target.value)}
                required
                className={inputClass}
              />
            </Field>

            <Field label={t('settings.gameAccounts.company')} error={errorFor('company')}>
              {/* Free text with common suggestions: new storefronts appear
                  constantly and a closed list would go stale. */}
              <input
                value={company}
                onChange={(event) => setCompany(event.target.value)}
                required
                list="gc-launcher-companies"
                className={inputClass}
              />
              <datalist id="gc-launcher-companies">
                {['Steam', 'Epic', 'Ubisoft', 'EA', 'Battle.net', 'GOG', 'Riot'].map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </Field>

            <Field label={t('common.username')} error={errorFor('username')}>
              <input
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                required
                dir="ltr"
                autoComplete="off"
                className={inputClass}
              />
            </Field>

            <Field
              label={t('common.password')}
              hint={isEdit ? t('settings.gameAccounts.leaveBlankToKeep') : undefined}
              error={errorFor('password')}
            >
              <input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required={!isEdit}
                autoComplete="new-password"
                className={inputClass}
              />
            </Field>

            <Field
              label={t('settings.gameAccounts.email')}
              hint={t('settings.gameAccounts.emailHint')}
              error={errorFor('email')}
            >
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                dir="ltr"
                autoComplete="off"
                className={inputClass}
              />
            </Field>

            {/* The clear box sits beside the field rather than inside it: `Field`
                is a <label>, and a second control inside one label would hand
                every click on the caption to whichever comes first. */}
            <div>
              <Field
                label={t('settings.gameAccounts.mailboxPassword')}
                hint={
                  isEdit && account?.hasEmailPassword
                    ? t('settings.gameAccounts.mailboxPasswordStored')
                    : t('settings.gameAccounts.mailboxPasswordHint')
                }
                error={errorFor('emailPassword')}
              >
                <input
                  type="password"
                  value={emailPassword}
                  onChange={(event) => setEmailPassword(event.target.value)}
                  disabled={clearEmailPassword}
                  autoComplete="new-password"
                  className={inputClass}
                />
              </Field>
              {isEdit && account?.hasEmailPassword ? (
                <label className="mt-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={clearEmailPassword}
                    onChange={(event) => setClearEmailPassword(event.target.checked)}
                    className="h-4 w-4 accent-[var(--brand-primary)]"
                  />
                  <span className="text-sm text-content">
                    {t('settings.gameAccounts.clearMailboxPassword')}
                  </span>
                </label>
              ) : null}
            </div>

            <div className="sm:col-span-2">
              <Field
                label={t('gameAccountTools.madeForPc')}
                hint={t('gameAccountTools.madeForPcHint')}
                error={errorFor('homeDeviceId')}
              >
                <select
                  value={homeDeviceId}
                  onChange={(event) => setHomeDeviceId(event.target.value)}
                  className={inputClass}
                  data-home-device
                >
                  <option value="">{t('gameAccountTools.venueWide')}</option>
                  {homeDeviceMissing ? (
                    <option value={homeDeviceId}>
                      {account?.homeDeviceId === homeDeviceId
                        ? (account.homeDeviceName ?? homeDeviceId)
                        : homeDeviceId}
                    </option>
                  ) : null}
                  {deviceGroups.map((group) => (
                    <optgroup
                      key={group.family ?? '*'}
                      label={group.family ?? t('gameAccountTools.otherDevices')}
                    >
                      {group.devices.map((device) => (
                        <option key={device.id} value={device.id}>
                          {device.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </Field>
            </div>

            <div className="sm:col-span-2">
              <Field
                label={t('settings.gameAccounts.notes')}
                hint={t('settings.gameAccounts.notesHint')}
                error={errorFor('notes')}
              >
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  rows={3}
                  maxLength={500}
                  className={inputClass}
                />
              </Field>
            </div>
          </div>

          <section className="mt-6">
            <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-content-secondary">
              {t('settings.gameAccounts.ownedGames')}
            </h3>

            {games.data === null ? (
              <p className="text-sm text-content-muted">{t('common.loading')}</p>
            ) : games.data.length === 0 ? (
              <p className="text-sm text-content-muted">{t('settings.gameAccounts.noGamesYet')}</p>
            ) : (
              <div className="space-y-2">
                {/* What the account owns decides Play on a Steam game: the
                    server picks, among this PC's own accounts, one linked to
                    the game (owner, 2026-10-05). */}
                {games.data.map((game) => (
                  <label
                    key={game.id}
                    className="flex items-center gap-2 rounded-control border border-subtle bg-input p-3"
                  >
                    <input
                      type="checkbox"
                      checked={gameIds.has(game.id)}
                      onChange={(event) => toggleGame(game.id, event.target.checked)}
                      className="h-4 w-4 accent-[var(--brand-primary)]"
                    />
                    <span className="text-sm text-content">{game.title}</span>
                  </label>
                ))}
              </div>
            )}
          </section>
        </div>

        <footer className="border-t border-subtle p-5">
          {errorFor('(root)') ? (
            <p role="alert" className="mb-3 text-sm text-danger">
              {errorFor('(root)')}
            </p>
          ) : null}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className={ghostPillClass}>
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={submitting} className={pillClass}>
              {t('common.save')}
            </button>
          </div>
        </footer>
      </form>
    </div>
  );
}
